import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  CloudUpload as UploadIcon,
  Check,
  Volume2,
  Video,
  Loader,
} from 'lucide-react';
import {
  UploadState,
  useIframeResizer,
  AppStoreBadge,
  GooglePlayBadge,
  finishProcessing,
} from './common';
import { useIntentTranslation, getIntentFromUrl } from './intentTranslations';
import config from '../../../config';
import uploadToScreenAppFileOpsV2, {
  finishProcessingWithRedirectV2,
} from './commonV2';
import { beforeFileUploadV2 } from './experimental/uncommon';
import { GetAnonymousDataResponseBody } from '../../services/anonService';

const BASE_MOBILE_URL = 'https://screenapp.io/mobile';

interface UploadProps {
  showIOSBadge?: boolean;
}

interface FilePreview {
  name: string;
  size: number;
  type: string;
  downloadUrl: string;
  blob?: Blob;
}

const Upload: React.FC<UploadProps> = ({ showIOSBadge = true }) => {
  const { t } = useTranslation();
  useIframeResizer();
  const [intent, setIntent] = useState<string | null>(getIntentFromUrl());
  const { t: intentT } = useIntentTranslation(intent, 'upload');
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    const handleHashChange = () => {
      const newIntent = getIntentFromUrl();
      setIntent(newIntent);
    };

    window.addEventListener('hashchange', handleHashChange);
    const currentIntent = getIntentFromUrl();
    if (currentIntent !== intent) {
      setIntent(currentIntent);
    }

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [intent]);

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(userAgent));
    setIsAndroid(/android/.test(userAgent));
  }, []);

  const [state, setState] = useState<UploadState>({
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [filePreview, setFilePreview] = useState<FilePreview | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Custom upload function that doesn't auto-redirect
  const uploadWithPreview = useCallback(
    async (
      file: File,
      onStart: () => void,
      onProgress: (progress: number) => void,
      onComplete: (downloadUrl: string) => void,
      onError: (error: string) => void,
    ) => {
      onStart();

      try {
        const sanitizedFileName = file.name
          .replace(/-/g, '_')
          .replace(/#/g, '')
          .replace(/[^a-zA-Z0-9._\s]/g, '')
          .trim();

        const response = await fetch(
          'https://api.screenapp.io/v2/temp-files/urls',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: sanitizedFileName }),
          },
        );

        const data = await response.json();
        if (data.success) {
          // Upload to S3 without auto-redirect
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', data.data.uploadUrl, true);
          xhr.setRequestHeader('Content-Type', file.type);

          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const progress = (event.loaded / event.total) * 100;
              onProgress(progress);
            }
          };

          xhr.onload = () => {
            if (xhr.status === 200) {
              onComplete(data.data.downloadUrl);
            } else {
              onError('Upload failed. Please try again.');
            }
          };

          xhr.onerror = () => {
            onError('Upload failed. Please try again.');
          };

          xhr.send(file);
        } else {
          throw new Error(data.message || 'Failed to get upload URL');
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        onError('Upload failed. Please try again.');
      }
    },
    [],
  );

  // V1 Upload Handler - Legacy API implementation
  const handleFileUploadV1 = useCallback(
    (file: File) => {
      uploadWithPreview(
        file,
        () =>
          setState((prev) => ({
            ...prev,
            uploading: true,
            progress: 0,
            error: null,
          })),
        (progress) => setState((prev) => ({ ...prev, progress })),
        (downloadUrl) => {
          setState((prev) => ({
            ...prev,
            uploadComplete: true,
            uploading: false,
          }));
          setFilePreview({
            name: file.name,
            size: file.size,
            type: file.type,
            downloadUrl,
            blob: file,
          });
        },
        (error) => setState((prev) => ({ ...prev, error, uploading: false })),
      );
    },
    [uploadWithPreview],
  );

  // V2 Upload Handler - New API implementation
  // Uses uploadToScreenAppFileOpsV2 with enhanced features and better error handling
  const handleFileUploadV2 = useCallback(async (file: File) => {
    let anonymousData: GetAnonymousDataResponseBody | null = null;
    if (config.enableUploadAnonymousUserAccount) {
      anonymousData = await beforeFileUploadV2();
    }
    uploadToScreenAppFileOpsV2(
      file,
      file.name,
      anonymousData,
      () =>
        setState((prev) => ({
          ...prev,
          uploading: true,
          progress: 0,
          error: null,
        })),
      (progress) => setState((prev) => ({ ...prev, progress })),
      (downloadUrl) => {
        setState((prev) => ({
          ...prev,
          uploadComplete: true,
          uploading: false,
        }));
        setFilePreview({
          name: file.name,
          size: file.size,
          type: file.type,
          downloadUrl,
          blob: file,
        });
      },
      (error) => setState((prev) => ({ ...prev, error, uploading: false })),
      !config.enableUploadAnonymousUserAccount, // enablePreview parameter
    );
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        const file = files[0];
        if (config.enableUploadMicroToolApiV2) {
          handleFileUploadV2(file);
        } else {
          handleFileUploadV1(file);
        }
      }
    },
    [handleFileUploadV1, handleFileUploadV2],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        const file = files[0];
        if (config.enableUploadMicroToolApiV2) {
          handleFileUploadV2(file);
        } else {
          handleFileUploadV1(file);
        }
      }
    },
    [handleFileUploadV1, handleFileUploadV2],
  );

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  // Auto-redirect after 1 second when upload is complete
  useEffect(() => {
    if (state.uploadComplete && filePreview?.downloadUrl) {
      const timer = setTimeout(() => {
        // Route to appropriate finish processing based on API version
        if (
          config.enableUploadMicroToolApiV2 &&
          config.enableUploadAnonymousUserAccount
        ) {
          // Do nothing, let the file processing handle appropriate redirect based on anonymous data
        } else if (config.enableUploadMicroToolApiV2) {
          finishProcessingWithRedirectV2(filePreview.downloadUrl, null);
        } else {
          finishProcessing(filePreview.downloadUrl);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [state.uploadComplete, filePreview]);

  // Get the mobile URL with intent parameter if it exists
  const getMobileUrl = () => {
    const hashPart = window.location.hash.split('?')[1] || '';
    const urlParams = new URLSearchParams(hashPart);
    const urlIntent = urlParams.get('intent') || '';

    const url = new URL(BASE_MOBILE_URL);
    url.searchParams.append('utm_source', urlIntent || 'website');
    url.searchParams.append('utm_medium', 'microtools');
    url.searchParams.append('utm_campaign', 'upload_cta');

    return url.toString();
  };

  // Show button interface on mobile devices
  if (isIOS || isAndroid) {
    return (
      <div className="flex justify-center">
        <div className="p-3 sm:p-6 max-w-5xl w-full">
          {/* Only show upload button when not uploading and not complete */}
          {!state.uploading && !state.uploadComplete && (
            <div
              className={`flex justify-center ${showIOSBadge ? 'mb-6' : 'mb-0'}`}
            >
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="h-12 flex items-center gap-2"
              >
                <UploadIcon className="w-4 h-4" />
                {intentT('mobileUploadButton').replace(/ /g, '\u00A0')}
              </Button>

              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept="video/*,audio/*,.m4a,audio/m4a,audio/x-m4a"
              />
            </div>
          )}

          {/* Minimal Upload Indicator */}
          {(state.uploading || state.uploadComplete) && (
            <div>
              <Card
                className={`mt-4 mb-4 rounded-xl ${
                  state.uploadComplete
                    ? 'bg-gradient-to-br from-green-50 to-green-100'
                    : 'bg-gradient-to-br from-blue-50 to-blue-100'
                }`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-lg bg-primary flex items-center justify-center">
                      {(() => {
                        if (state.uploading) {
                          return (
                            <Loader className="w-6 h-6 animate-spin text-white" />
                          );
                        }
                        if (filePreview) {
                          if (filePreview.type.startsWith('video/')) {
                            return <Video className="w-8 h-8 text-white" />;
                          }
                          if (filePreview.type.startsWith('audio/')) {
                            return <Volume2 className="w-8 h-8 text-white" />;
                          }
                          return <Check className="w-8 h-8 text-white" />;
                        }
                        return <Check className="w-8 h-8 text-white" />;
                      })()}
                    </div>

                    <div className="flex-1 min-w-0">
                      {(() => {
                        if (state.uploading) {
                          return (
                            <>
                              <p className="text-base font-medium mb-1">
                                {intentT('uploading')}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {Math.round(state.progress)}% complete
                              </p>
                            </>
                          );
                        }
                        if (filePreview) {
                          return (
                            <>
                              <p className="text-sm font-medium mb-1 truncate">
                                {filePreview.name}
                              </p>
                              <div className="flex items-center gap-2">
                                <p className="text-xs text-muted-foreground">
                                  {formatFileSize(filePreview.size)} •{' '}
                                  {filePreview.type.split('/')[1].toUpperCase()}{' '}
                                  • {intentT('processing')}
                                </p>
                                <Loader className="w-3 h-3 animate-spin text-muted-foreground" />
                              </div>
                            </>
                          );
                        }
                        return (
                          <p className="text-base font-medium">
                            {intentT('complete')}
                          </p>
                        );
                      })()}
                    </div>
                  </div>

                  {state.uploading && (
                    <Progress value={state.progress} className="h-1.5" />
                  )}
                </CardContent>
              </Card>

              {/* Cancel button */}
              <div className="flex justify-center mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setState({
                      uploading: false,
                      progress: 0,
                      uploadComplete: false,
                      error: null,
                    });
                    setFilePreview(null);
                  }}
                  className="text-xs text-muted-foreground"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {state.error && (
            <p className="text-destructive text-center mb-4">{state.error}</p>
          )}

          {/* Show mobile badge below on separate row */}
          {showIOSBadge && !state.uploading && !state.uploadComplete && (
            <div className="flex justify-center">
              <div className="flex flex-col items-center gap-2">
                <p className="text-xs text-muted-foreground">
                  {t('tools.mobileappcta.orUseApp')}
                </p>

                {isIOS && (
                  <AppStoreBadge
                    mobileUrl={getMobileUrl()}
                    showLabel={false}
                    height={44}
                  />
                )}

                {isAndroid && (
                  <GooglePlayBadge
                    mobileUrl={getMobileUrl()}
                    showLabel={false}
                    height={44}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show drag-and-drop interface on desktop
  return (
    <div className="flex justify-center">
      <div className="p-3 sm:p-6 max-w-5xl w-full">
        {/* Only show upload interface when not uploading and not complete */}
        {!state.uploading && !state.uploadComplete && (
          <div className="flex justify-center">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              accept="video/*,audio/*,.m4a,audio/m4a,audio/x-m4a"
            />

            <div
              className={`border-[3px] border-dashed rounded-xl p-8 sm:p-16 transition-all duration-200 cursor-pointer w-full flex flex-col items-center gap-4 ${
                isDragging
                  ? 'border-primary bg-accent'
                  : 'border-muted-foreground/40 hover:border-primary hover:bg-accent'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Click to select file or drag and drop file here"
            >
              <h3 className="text-lg font-medium text-foreground">
                {intentT('desktopTitle')}
              </h3>

              <Button
                size="lg"
                className="flex items-center gap-2 px-8 py-3 text-base"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                <UploadIcon className="w-4 h-4" />
                {intentT('desktopUploadButton')}
              </Button>
            </div>
          </div>
        )}

        {/* Minimal Upload Indicator for Desktop */}
        {(state.uploading || state.uploadComplete) && (
          <div>
            <Card
              className={`mt-6 rounded-xl ${
                state.uploadComplete
                  ? 'bg-gradient-to-br from-green-50 to-green-100'
                  : 'bg-gradient-to-br from-blue-50 to-blue-100'
              }`}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-6 mb-4">
                  <div className="w-20 h-20 rounded-lg bg-primary flex items-center justify-center">
                    {(() => {
                      if (state.uploading) {
                        return (
                          <Loader className="w-8 h-8 animate-spin text-white" />
                        );
                      }
                      if (filePreview) {
                        if (filePreview.type.startsWith('video/')) {
                          return <Video className="w-10 h-10 text-white" />;
                        }
                        if (filePreview.type.startsWith('audio/')) {
                          return <Volume2 className="w-10 h-10 text-white" />;
                        }
                        return <Check className="w-10 h-10 text-white" />;
                      }
                      return <Check className="w-10 h-10 text-white" />;
                    })()}
                  </div>

                  <div className="flex-1 min-w-0">
                    {(() => {
                      if (state.uploading) {
                        return (
                          <>
                            <p className="text-lg font-medium mb-1">
                              {intentT('uploading')}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {Math.round(state.progress)}% complete
                            </p>
                          </>
                        );
                      }
                      if (filePreview) {
                        return (
                          <>
                            <p className="text-lg font-medium mb-1 truncate">
                              {filePreview.name}
                            </p>
                            <div className="flex items-center gap-2">
                              <p className="text-sm text-muted-foreground">
                                {formatFileSize(filePreview.size)} •{' '}
                                {filePreview.type.split('/')[1].toUpperCase()}{' '}
                                file • {intentT('processing')}
                              </p>
                              <Loader className="w-3 h-3 animate-spin text-muted-foreground" />
                            </div>
                          </>
                        );
                      }
                      return (
                        <p className="text-lg font-medium">
                          {intentT('complete')}
                        </p>
                      );
                    })()}
                  </div>
                </div>

                {state.uploading && (
                  <Progress value={state.progress} className="h-2" />
                )}
              </CardContent>
            </Card>

            {/* Cancel button */}
            <div className="flex justify-center mt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setState({
                    uploading: false,
                    progress: 0,
                    uploadComplete: false,
                    error: null,
                  });
                  setFilePreview(null);
                }}
                className="text-xs text-muted-foreground"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {state.error && (
          <p className="text-destructive mt-4 text-sm">{state.error}</p>
        )}
      </div>
    </div>
  );
};

export default Upload;
